"use client";

import React, { useState, useEffect, useCallback, useRef, MouseEventHandler } from "react";
import styled from "styled-components";
import AutosizeTextarea from "react-textarea-autosize";

import { getFont, getColor, getBorderRadius } from "../utils";

import Text from "../Text";
import Box from "../Box";

import focusEndOfInput, { focusAllOfInput } from "../../helpers/focusEndOfInput";
import useSubmitTextKeys from "../../hooks/useSubmitTextKeys";
import { AnyObject } from "../../models";
import { ElementPositioningProps } from "../ComponentProps";

export const headingTestId = "gallery-heading-text";
export const wrapperTestId = "gallery-heading-wrapper";
export const editorTestId = "gallery-heading-editor";

type HeadingItem = {
  uuid: string | number;
  data: {
    content?: string;
  };
  shortId?: string;
};

type Props<Item extends HeadingItem> = {
  item: Item;
  selected?: boolean;
  canEdit?: boolean;
  isEditing?: boolean;
  hasDefaultValue?: boolean;
  onBeginEditing?: (itemId: Item["uuid"]) => void;
  onFinishEditing?: (item: Item, data: null | { data: { content: string } }) => void;
  "data-itemid"?: string;
  onClick?: MouseEventHandler;
} & ElementPositioningProps;

type WrapperProps = { canEdit: boolean; selected?: boolean; itemId?: string };

const Wrapper = styled(Box).attrs<WrapperProps>(props => ({
  mx: "s",
  flex: "1 1 100%",
  border:
    props.selected && props.canEdit
      ? `2px solid ${getColor(props.theme.primaryColor, "primary")}`
      : "2px solid transparent",
  borderRadius: "default",
}))<WrapperProps>`
  position: "relative";
`;

const HeadingText = styled(Text).attrs<WrapperProps>({
  as: "h2",
  font: "gallery.heading",
  color: "black",
  borderRadius: "default",
  px: "xs",
})<WrapperProps>`
  word-break: break-word;
  ${props =>
    props.canEdit &&
    `
    cursor: pointer;
  `}
  &:first-child {
    margin-top: 0;
  }
`;

const Textarea = styled(AutosizeTextarea)`
  min-height: 42px;
  padding: 0;
  border-radius: ${getBorderRadius("default")};
  border: none;
  resize: none;
  width: 100%;
  ${props => getFont(null, "gallery.heading", props)}

  /*
  * Safari kill x margin
  */
  margin: 0;

  /*
  * Firefox inherit font-feature settings
  */
  font-feature-settings: inherit;

  &:focus {
    outline: none;
  }
`;

const GalleryHeading = <Item extends HeadingItem>({
  item,
  selected,
  canEdit,
  isEditing,
  hasDefaultValue,
  onBeginEditing,
  onFinishEditing,
  "data-itemid": itemId,
  onClick,
  ...rest
}: Props<Item>) => {
  const [editValue, setEditValue] = useState(item.data.content);

  // Refs
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const beginEditing = useCallback(() => {
      if (onBeginEditing) onBeginEditing(item.uuid);
    }, [item, onBeginEditing]),
    finishEditing = useCallback(() => {
      if (!isEditing || !canEdit || !onFinishEditing) return;

      // Do not submit if the original content hasn't changed
      const val = editValue.trim();
      if (item.data.content === val) return onFinishEditing(item, null);
      const data = {
        data: {
          content: val,
        },
      };
      onFinishEditing(item, data);
    }, [isEditing, canEdit, onFinishEditing, item, editValue]);

  /**
   * Every time the edit mode changes, make sure the latest version
   * of the content prop is hydrated in the editValue state variable.
   *
   * If opening edit mode, select the text within the textarea for manipulation.
   */
  useEffect(() => {
    setEditValue(item.data.content);
    if (isEditing && editorRef.current) {
      (hasDefaultValue ? focusAllOfInput : focusEndOfInput)(editorRef.current);
    }
  }, [hasDefaultValue, isEditing, item.data.content]);

  const onKeyDown = useSubmitTextKeys(false);

  const headingTextProps = {
    canEdit,
    selected,
    onDoubleClick: canEdit ? beginEditing : undefined,
  };

  return (
    <Wrapper
      id={item.shortId}
      data-testid={wrapperTestId}
      data-itemid={itemId}
      canEdit={canEdit}
      selected={selected}
      onClick={onClick}
      {...rest}>
      {isEditing ? (
        <Textarea
          aria-label="Update heading text"
          data-testid={editorTestId}
          ref={editorRef}
          value={editValue}
          onBlur={finishEditing}
          onChange={e => setEditValue((e.target as AnyObject).value as string)}
          onKeyDown={onKeyDown}
        />
      ) : (
        <HeadingText {...headingTextProps} data-testid={headingTestId}>
          {item.data.content}
        </HeadingText>
      )}
    </Wrapper>
  );
};

export default GalleryHeading;
